import { useTrans } from "@/utils/useTrans";
import Image from "next/image";
import { cn } from "@/libs/utils";

type SpecItem = {
  label: string;
  values: string[];
  caption?: string[];
  /** Figma 모바일에서 hidden 처리된 항목. ACTUATORS/PLACEMENT/LATENCY 가 해당. */
  desktopOnly?: boolean;
};

export default function Dk3Specs() {
  const { t } = useTrans(["tactsuit"]);

  const items: SpecItem[] = [
    {
      label: t("HAPTIC POINTS"),
      values: [t("8 per hand"), "(16 per pair)"],
    },
    {
      label: t("ACTUATORS (PER HAND)"),
      values: ["7 x high-definition LRA", "1 x high-fidelity VCM"],
      desktopOnly: true,
    },
    {
      label: t("PLACEMENT (PER HAND)"),
      values: ["5 x Fingertips", "2 x Palm", "1 x Wrist"],
      desktopOnly: true,
    },
    {
      label: t("CHARGING TIME"),
      values: [t("2 Hrs")],
      caption: [
        t("Full charge time with 5V 0.5A (Max)"),
        t("Li-ion rechargeable battery"),
        "(3.8V, 630mAh, 2.394Wh)",
      ],
    },
    { label: t("LATENCY"), values: ["< 20 ms"], desktopOnly: true },
    { label: t("CONNECTION"), values: [t("Bluetooth LE")] },
    { label: t("PLAYTIME"), values: [t("630 mAh · 4 hrs")] },
    {
      label: t("TRACKING"),
      values: [t("Compatible with camera-based hand tracking systems")],
    },
  ];

  return (
    <section className="w-full bg-white">
      {/* Outer header */}
      <div className="mx-auto max-w-screen-max px-4 laptop:px-8">
        <h2 className="text-center text-[24px] font-400 leading-8 text-coolgray-90 laptop:text-[32px] laptop:leading-10">
          {t("Engineering Specs")}
        </h2>
      </div>

      {/* Inner body — full-bleed coolgray-10 */}
      <div className="flex w-full flex-col items-center gap-6 py-16 pb-0 pt-6 laptop:mt-10 laptop:gap-10 laptop:pt-24">
        {/* 데스크톱: front + back 통합 이미지 (callout 라벨/라인 베이크). */}
        <div className="hidden w-full items-center justify-center laptop:flex">
          <div className="relative h-[360px] w-[789px]">
            <Image
              src="/images/tactsuit/tactglove-dk3/engineering-specs-desktop.webp"
              alt="DK3 schematic — front and back"
              fill
              sizes="789px"
              className="object-contain"
            />
          </div>
        </div>

        {/* 모바일: native scroll-snap (front/back 2장).
            카드 1:1 (280/280), snap 좌측 16px 고정. */}
        <div className="w-full snap-x snap-mandatory scroll-pl-4 overflow-x-auto pl-4 pr-4 [scrollbar-width:none] laptop:hidden [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-4">
            <div className="w-[85vw] shrink-0 snap-start">
              <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-coolgray-20 bg-coolgray-10">
                <div className="absolute inset-4">
                  <Image
                    src="/images/tactsuit/tactglove-dk3/engineering-specs-front.png"
                    alt="DK3 schematic — front"
                    fill
                    sizes="85vw"
                    className="object-contain"
                  />
                </div>
              </div>
            </div>
            <div className="w-[85vw] shrink-0 snap-start">
              <div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-coolgray-20 bg-coolgray-10">
                <div className="absolute inset-4">
                  <Image
                    src="/images/tactsuit/tactglove-dk3/engineering-specs-back.png"
                    alt="DK3 schematic — back"
                    fill
                    sizes="85vw"
                    className="object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Spec grid — 모바일 1열, 데스크톱 4열 */}
        <div className="grid w-full max-w-[1196px] grid-cols-1 gap-y-8 px-6 laptop:grid-cols-4 laptop:gap-x-0 laptop:gap-y-0">
          {items.map((item, i) => (
            <div
              key={item.label}
              className={cn(
                "flex flex-col items-start gap-2",
                item.desktopOnly && "hidden laptop:flex",
                // 데스크톱 4컬럼 사이 세로 구분선 (col1 제외). gap-y-0 + min-h 로 행 간 연속.
                i % 4 !== 0 &&
                  "laptop:border-l laptop:border-coolgray-20 laptop:pl-6",
                i < 4 && "laptop:min-h-[160px]",
              )}
            >
              <span className="text-base font-600 leading-[18px] tracking-[0.25px] text-coolgray-90">
                {item.label}
              </span>
              <div className="flex flex-col text-lg font-400 leading-[22px] tracking-[0.5px] text-coolgray-90">
                {item.values.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </div>
              {item.caption && (
                <div className="flex flex-col text-base font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
                  {item.caption.map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
